import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AXES,
  type AxisId,
  type CutPiece,
  type PipeRun,
  type RunFitting,
  type Vec3,
} from "@/hooks/use-pipe-sketch";
import { formatFtIn, getSizeSpec, type MaterialId } from "@/lib/pipe-spec";

const COS30 = Math.cos(Math.PI / 6);
const SIN30 = 0.5;
/** drawing snap increment, inches */
const SNAP = 3;
/** background grid spacing, inches */
const GRID = 12;
/** pointer distance (px) for snapping a branch onto an existing run */
const BRANCH_HIT = 14;

export interface Screen {
  x: number;
  y: number;
}

export const project = (p: Vec3, scale: number): Screen => ({
  x: (p.x - p.y) * COS30 * scale,
  y: ((p.x + p.y) * SIN30 - p.z) * scale,
});

/** Screen-space unit direction of each pipe axis. */
const axisScreen: Record<AxisId, Screen> = {
  "x+": { x: COS30, y: SIN30 },
  "x-": { x: -COS30, y: -SIN30 },
  "y+": { x: -COS30, y: SIN30 },
  "y-": { x: COS30, y: -SIN30 },
  "z+": { x: 0, y: -1 },
  "z-": { x: 0, y: 1 },
};

const axisLabel: Record<AxisId, string> = {
  "x+": "EAST",
  "x-": "WEST",
  "y+": "NORTH",
  "y-": "SOUTH",
  "z+": "UP",
  "z-": "DOWN",
};

const add = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const scaleV = (v: Vec3, s: number): Vec3 => ({ x: v.x * s, y: v.y * s, z: v.z * s });
const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const lenV = (v: Vec3) => Math.hypot(v.x, v.y, v.z);
const unit = (v: Vec3): Vec3 => {
  const l = lenV(v) || 1;
  return scaleV(v, 1 / l);
};

export type SketchMode = "draw" | "branch" | "pan";

interface Props {
  runs: PipeRun[];
  fittings: RunFitting[];
  cutList: CutPiece[];
  activeRunId: string | null;
  material: MaterialId;
  size: string;
  mode: SketchMode;
  onStart: (p: Vec3) => void;
  onBranch: (hostRunId: string, p: Vec3) => void;
  onExtend: (p: Vec3) => void;
  onFinish: () => void;
  onUndoVertex: () => void;
}

export default function IsoSketchCanvas({
  runs,
  fittings,
  cutList,
  activeRunId,
  material,
  size,
  mode,
  onStart,
  onBranch,
  onExtend,
  onFinish,
  onUndoVertex,
}: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [box, setBox] = useState({ w: 800, h: 520 });
  const [scale, setScale] = useState(3.2); // px per inch
  const [pan, setPan] = useState<Screen>({ x: 0, y: 0 });
  const [cursor, setCursor] = useState<Screen | null>(null);
  const panRef = useRef<{ start: Screen; pan: Screen } | null>(null);
  /** touch/pen input gets bigger hit areas and bigger markers */
  const [coarse, setCoarse] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches,
  );
  /** px radius for snapping onto vertices and runs */
  const hitR = coarse ? BRANCH_HIT * 2.4 : BRANCH_HIT;
  /** marker size multiplier */
  const mk = coarse ? 1.8 : 1;
  const [showHelp, setShowHelp] = useState(false);
  /** Auto-hide timer for the gesture overlay. */
  const helpTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearHelpTimer = () => {
    if (helpTimer.current) clearTimeout(helpTimer.current);
    helpTimer.current = null;
  };
  const openHelp = () => {
    clearHelpTimer();
    setShowHelp(true);
    helpTimer.current = setTimeout(() => setShowHelp(false), 8000);
  };
  const closeHelp = () => {
    clearHelpTimer();
    setShowHelp(false);
  };
  useEffect(() => clearHelpTimer, []);
  /** ISO weld symbology (solid butt-weld dots) */
  const [showWelds, setShowWelds] = useState(true);

  /** Gesture cheat-sheet for the active mode, tailored to touch vs mouse. */
  const modeHelp = useMemo(() => {
    const zoom = coarse
      ? ["Pinch with two fingers to zoom", "Tap + / − / FIT for precise zoom and framing"]
      : ["Scroll wheel to zoom", "Click + / − / FIT for precise zoom and framing"];
    const pan = coarse
      ? ["Drag one finger on empty space to pan", "Two-finger drag also pans"]
      : ["Shift-drag or middle-mouse drag to pan"];
    if (mode === "pan") {
      return {
        title: "Pan mode — view only",
        lines: [
          coarse ? "Drag anywhere to move the sheet" : "Drag anywhere to move the sheet",
          "Nothing is drawn while Pan is active",
          ...zoom,
          "Switch to Draw or Branch to keep sketching",
        ],
      };
    }
    if (mode === "branch") {
      return {
        title: "Branch mode — tee off a run",
        lines: [
          coarse ? "Tap a diamond marker on a pipe to start a branch" : "Click a diamond marker on a pipe to start a branch",
          "Branches lock 90° to the host run",
          ...pan,
          ...zoom,
        ],
      };
    }
    return {
      title: "Draw mode — run pipe",
      lines: [
        coarse ? "Tap to set the start point, tap again along an axis" : "Click to set the start point, click again along an axis",
        "Turns auto-insert a BW LR elbow",
        "Enter or Esc finishes the run · Backspace undoes a vertex",
        ...pan,
        ...zoom,
      ],
    };
  }, [mode, coarse]);




  useEffect(() => {
    const el = svgRef.current?.parentElement;
    if (!el) return;
    const ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setBox({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  const origin = useMemo(
    () => ({ x: box.w / 2 + pan.x, y: box.h / 2 + pan.y }),
    [box, pan],
  );

  const toScreen = useCallback(
    (p: Vec3): Screen => {
      const s = project(p, scale);
      return { x: origin.x + s.x, y: origin.y + s.y };
    },
    [origin, scale],
  );

  const activeRun = runs.find((r) => r.id === activeRunId) ?? null;
  const lastPt = activeRun?.pts[activeRun.pts.length - 1] ?? null;

  /** Pointer -> point on the z = 0 plane, snapped to the grid. */
  const groundPoint = useCallback(
    (sx: number, sy: number): Vec3 => {
      const dx = (sx - origin.x) / scale;
      const dy = (sy - origin.y) / scale;
      const a = dx / COS30; // x - y
      const b = dy / SIN30; // x + y
      const x = Math.round(((a + b) / 2) / GRID) * GRID;
      const y = Math.round(((b - a) / 2) / GRID) * GRID;
      return { x, y, z: 0 };
    },
    [origin, scale],
  );

  /**
   * Branch/tee target. Existing interior vertices win over mid-segment points so a
   * branch always lands on real geometry; mid-segment hits snap to the 3" increment
   * and keep enough clearance from each end for the neighbouring fitting takeoffs.
   */
  const branchTarget = useMemo(() => {
    if (mode !== "branch" || !cursor || lastPt) return null;
    const branchSpec = getSizeSpec(material, size);
    let best: { runId: string; at: Vec3; d: number; snapped: "vertex" | "run" } | null = null;
    const consider = (c: {
      runId: string;
      at: Vec3;
      d: number;
      snapped: "vertex" | "run";
    }) => {
      // bias towards existing vertices
      const score = c.d - (c.snapped === "vertex" ? hitR * 0.6 : 0);
      const bestScore = best ? best.d - (best.snapped === "vertex" ? hitR * 0.6 : 0) : Infinity;
      if (score < bestScore) best = c;
    };

    for (const run of runs) {
      // 1. interior vertices (a tee can't sit on a free end)
      for (let i = 1; i < run.pts.length - 1; i++) {
        const sv = toScreen(run.pts[i]);
        const d = Math.hypot(cursor.x - sv.x, cursor.y - sv.y);
        if (d <= hitR) {
          consider({ runId: run.id, at: run.pts[i], d, snapped: "vertex" });
        }
      }

      // 2. points along each straight
      for (let i = 0; i < run.pts.length - 1; i++) {
        const a = run.pts[i];
        const b = run.pts[i + 1];
        const sa = toScreen(a);
        const sb = toScreen(b);
        const vx = sb.x - sa.x;
        const vy = sb.y - sa.y;
        const l2 = vx * vx + vy * vy || 1;
        let t = ((cursor.x - sa.x) * vx + (cursor.y - sa.y) * vy) / l2;
        t = Math.max(0, Math.min(1, t));
        const px = sa.x + vx * t;
        const py = sa.y + vy * t;
        const d = Math.hypot(cursor.x - px, cursor.y - py);
        if (d > hitR) continue;

        const total = lenV(sub(b, a));
        const hostSpec = getSizeSpec(material, run.segSizes[i] ?? run.size);
        // keep the tee clear of whatever sits at each end of this straight
        const clear =
          Math.ceil((hostSpec.teeRun + Math.max(hostSpec.elbow90, branchSpec.teeBranch)) / SNAP) *
          SNAP;
        const lo = clear;
        const hi = total - clear;
        if (hi < lo) continue;
        const along = Math.min(hi, Math.max(lo, Math.round((t * total) / SNAP) * SNAP));
        const at = add(a, scaleV(unit(sub(b, a)), along));
        consider({ runId: run.id, at, d, snapped: "run" });
      }
    }
    return best as { runId: string; at: Vec3; d: number; snapped: "vertex" | "run" } | null;
  }, [mode, cursor, lastPt, runs, toScreen, material, size, hitR]);

  /**
   * Axes the next click may use. A branch's first leg is locked square to its
   * host run so the derived tee is always a true 90° take-off.
   */
  const allowedAxes = useMemo<AxisId[]>(() => {
    const all = Object.keys(axisScreen) as AxisId[];
    if (!activeRun || activeRun.pts.length !== 1) return all;
    const hd = activeRun.branch?.hostDir;
    if (!hd) return all;
    return all.filter((a) => {
      const v = AXES[a];
      return Math.abs(v.x * hd.x + v.y * hd.y + v.z * hd.z) < 0.01;
    });
  }, [activeRun]);

  /** Preview point constrained to the closest pipe axis. */
  const preview = useMemo(() => {
    if (!lastPt || !cursor) return null;
    const from = toScreen(lastPt);
    const dx = cursor.x - from.x;
    const dy = cursor.y - from.y;
    let best: { axis: AxisId; inches: number } | null = null;
    allowedAxes.forEach((axis) => {
      const d = axisScreen[axis];
      const proj = (dx * d.x + dy * d.y) / scale;
      if (proj <= 0) return;
      if (!best || proj > best.inches) best = { axis, inches: proj };
    });
    if (!best) return null;
    const inches = Math.max(SNAP, Math.round(best.inches / SNAP) * SNAP);
    const to = add(lastPt, scaleV(AXES[best.axis], inches));
    return { axis: best.axis, inches, to };
  }, [lastPt, cursor, toScreen, scale, allowedAxes]);

  /** Dashed rays along every axis the next click may lock to, with 12" ticks. */
  const axisGuides = useMemo(() => {
    if (!lastPt) return [];
    const from = toScreen(lastPt);
    const reach = Math.max(box.w, box.h);
    return allowedAxes.map((axis) => {
      const d = axisScreen[axis];
      const ticks: Screen[] = [];
      const stepPx = GRID * scale;
      if (stepPx > 8) {
        for (let px = stepPx; px < reach; px += stepPx) {
          ticks.push({ x: from.x + d.x * px, y: from.y + d.y * px });
        }
      }
      return {
        axis,
        from,
        to: { x: from.x + d.x * reach, y: from.y + d.y * reach },
        ticks,
        active: preview?.axis === axis,
      };
    });
  }, [lastPt, toScreen, allowedAxes, box, scale, preview]);

  /** Turn marker at the working end: arc + the fitting angle the next click makes. */
  const turnMarker = useMemo(() => {
    if (!activeRun || !lastPt || !preview || activeRun.pts.length < 2) return null;
    const prev = activeRun.pts[activeRun.pts.length - 2];
    const inDir = unit(sub(lastPt, prev));
    const outDir = AXES[preview.axis];
    const dot = inDir.x * outDir.x + inDir.y * outDir.y + inDir.z * outDir.z;
    const angle = Math.round((Math.acos(Math.max(-1, Math.min(1, dot))) * 180) / Math.PI);
    if (angle === 0) return { angle: 0, label: "STRAIGHT", d: "", at: toScreen(lastPt) };
    const c = toScreen(lastPt);
    const r = 16;
    const a = toScreen(sub(lastPt, scaleV(inDir, r / scale)));
    const b = toScreen(add(lastPt, scaleV(outDir, r / scale)));
    const ax = { x: c.x - (c.x - a.x), y: c.y - (c.y - a.y) };
    return {
      angle,
      label: `${angle}° TURN`,
      d: `M ${ax.x} ${ax.y} Q ${c.x} ${c.y} ${b.x} ${b.y}`,
      at: c,
    };
  }, [activeRun, lastPt, preview, toScreen, scale]);

  /** Every vertex a branch can legally lock onto (branch mode, before drawing). */
  const branchVertices = useMemo(() => {
    if (mode !== "branch" || lastPt) return [];
    const out: Screen[] = [];
    for (const run of runs) {
      for (let i = 1; i < run.pts.length - 1; i++) out.push(toScreen(run.pts[i]));
    }
    return out;
  }, [mode, lastPt, runs, toScreen]);

  /** latest derived values, readable from pointer handlers without stale closures */
  const previewRef = useRef(preview);
  previewRef.current = preview;
  const branchTargetRef = useRef(branchTarget);
  branchTargetRef.current = branchTarget;
  const lastPtRef = useRef(lastPt);
  lastPtRef.current = lastPt;

  /** Commit a tap/click at a canvas position. */
  const commitAt = useCallback(
    (sx: number, sy: number) => {
      if (mode === "pan") return;
      if (!lastPtRef.current) {
        if (mode === "branch") {
          const bt = branchTargetRef.current;
          if (bt) onBranch(bt.runId, bt.at);
          return;
        }
        onStart(groundPoint(sx, sy));
      } else if (previewRef.current) {
        onExtend(previewRef.current.to);
      }
    },
    [groundPoint, onStart, onExtend, mode, onBranch],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Enter") onFinish();
      if (e.key === "Backspace" && activeRunId) {
        e.preventDefault();
        onUndoVertex();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onFinish, onUndoVertex, activeRunId]);

  const onWheel = (e: React.WheelEvent) => {
    setScale((s) => Math.min(14, Math.max(0.6, s * (e.deltaY > 0 ? 0.9 : 1.1))));
  };

  const zoomBy = (f: number) =>
    setScale((s) => Math.min(14, Math.max(0.6, s * f)));

  /** Frame all drawn geometry, or reset the view when the sheet is empty. */
  const fitView = useCallback(() => {
    const pts = runs.flatMap((r) => r.pts);
    if (!pts.length) {
      setScale(3.2);
      setPan({ x: 0, y: 0 });
      return;
    }
    const pj = pts.map((p) => project(p, 1));
    const minX = Math.min(...pj.map((p) => p.x));
    const maxX = Math.max(...pj.map((p) => p.x));
    const minY = Math.min(...pj.map((p) => p.y));
    const maxY = Math.max(...pj.map((p) => p.y));
    const w = Math.max(maxX - minX, 1);
    const h = Math.max(maxY - minY, 1);
    const s = Math.min(14, Math.max(0.6, Math.min((box.w - 90) / w, (box.h - 90) / h)));
    setScale(s);
    setPan({ x: -((minX + maxX) / 2) * s, y: -((minY + maxY) / 2) * s });
  }, [runs, box]);


  const pointers = useRef(new Map<number, Screen>());
  const pinchRef = useRef<{ dist: number; scale: number } | null>(null);
  const tapRef = useRef<{ x: number; y: number; moved: boolean } | null>(null);

  const localPos = (e: { clientX: number; clientY: number }): Screen => {
    const rect = svgRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const twoFingerDist = () => {
    const [a, b] = [...pointers.current.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    // any canvas gesture dismisses the cheat sheet
    if (showHelp) closeHelp();
    if (e.pointerType !== "mouse") setCoarse(true);
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    // touch has no hover: place the cursor on touch-down so the preview exists
    if (mode !== "pan") setCursor(localPos(e));

    if (pointers.current.size === 2) {
      pinchRef.current = { dist: twoFingerDist(), scale };
      tapRef.current = null;
      panRef.current = { start: { x: e.clientX, y: e.clientY }, pan };
      return;
    }
    if (mode === "pan" || e.button === 1 || e.shiftKey) {
      panRef.current = { start: { x: e.clientX, y: e.clientY }, pan };
      tapRef.current = null;
      return;
    }
    tapRef.current = { x: e.clientX, y: e.clientY, moved: false };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (pointers.current.has(e.pointerId)) {
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }

    if (pointers.current.size === 2 && pinchRef.current) {
      const d = twoFingerDist();
      const ratio = d / (pinchRef.current.dist || 1);
      setScale(Math.min(14, Math.max(0.6, pinchRef.current.scale * ratio)));
      return;
    }

    if (mode !== "pan") setCursor(localPos(e));

    const tap = tapRef.current;
    if (tap && !tap.moved) {
      const slop = e.pointerType === "mouse" ? 6 : 14;
      const moved = Math.hypot(e.clientX - tap.x, e.clientY - tap.y) > slop;
      if (moved) {
        tap.moved = true;
        // a finger drag pans the view; a tap draws
        if (e.pointerType !== "mouse" && !panRef.current) {
          panRef.current = { start: { x: tap.x, y: tap.y }, pan };
        }
      }
    }

    if (panRef.current) {
      setPan({
        x: panRef.current.pan.x + (e.clientX - panRef.current.start.x),
        y: panRef.current.pan.y + (e.clientY - panRef.current.start.y),
      });
    }
  };

  const endPointer = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchRef.current = null;

    const tap = tapRef.current;
    tapRef.current = null;
    const wasPanning = !!panRef.current;
    panRef.current = null;

    if (tap && !tap.moved && !wasPanning) {
      const p = localPos(e);
      commitAt(p.x, p.y);
      if (e.pointerType !== "mouse") setCursor(p);
    }
  };


  /** Background isometric grid on the ground plane. */
  const gridLines = useMemo(() => {
    const n = 14;
    const lines: { a: Screen; b: Screen }[] = [];
    for (let i = -n; i <= n; i++) {
      lines.push({
        a: toScreen({ x: i * GRID, y: -n * GRID, z: 0 }),
        b: toScreen({ x: i * GRID, y: n * GRID, z: 0 }),
      });
      lines.push({
        a: toScreen({ x: -n * GRID, y: i * GRID, z: 0 }),
        b: toScreen({ x: n * GRID, y: i * GRID, z: 0 }),
      });
    }
    return lines;
  }, [toScreen]);

  const pieceOf = useCallback(
    (runId: string, index: number) => cutList.find((c) => c.runId === runId && c.index === index),
    [cutList],
  );

  /**
   * Single-line ISO path for a run: straights trimmed back by each fitting's
   * takeoff, with an arc through every turn.
   */
  const runPath = useCallback(
    (run: PipeRun) => {
      const segs: string[] = [];
      const pts = run.pts;
      for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i];
        const b = pts[i + 1];
        const total = lenV(sub(b, a));
        const dir = unit(sub(b, a));
        const piece = pieceOf(run.id, i);
        const startTrim = Math.min(piece?.trimStart ?? 0, total / 2);
        const endTrim = Math.min(piece?.trimEnd ?? 0, total / 2);
        const p1 = toScreen(add(a, scaleV(dir, startTrim)));
        const p2 = toScreen(sub(b, scaleV(dir, endTrim)));
        const elbowHere = fittings.some(
          (f) => f.runId === run.id && f.index === i && f.kind.startsWith("elbow"),
        );
        if (elbowHere && i > 0) {
          const prev = pts[i - 1];
          const pdir = unit(sub(a, prev));
          const prevPiece = pieceOf(run.id, i - 1);
          const arcStart = toScreen(sub(a, scaleV(pdir, prevPiece?.trimEnd ?? 0)));
          const corner = toScreen(a);
          segs.push(`M ${arcStart.x} ${arcStart.y} Q ${corner.x} ${corner.y} ${p1.x} ${p1.y}`);
        }
        segs.push(`M ${p1.x} ${p1.y} L ${p2.x} ${p2.y}`);
      }
      return segs.join(" ");
    },
    [fittings, toScreen, pieceOf],
  );

  /**
   * Butt-weld locations, per single-line ISO convention: a weld sits wherever a
   * straight piece meets a fitting take-off (pipe end trimmed back). Drawn as a
   * solid dot — the standard shop butt-weld symbol.
   */
  const weldPoints = useMemo(() => {
    if (!showWelds) return [] as Screen[];
    const seen = new Set<string>();
    const out: Screen[] = [];
    const push = (p: Vec3) => {
      const s = toScreen(p);
      const k = `${Math.round(s.x)}|${Math.round(s.y)}`;
      if (seen.has(k)) return;
      seen.add(k);
      out.push(s);
    };
    for (const piece of cutList) {
      const total = lenV(sub(piece.to, piece.from));
      if (total <= 0) continue;
      const dir = unit(sub(piece.to, piece.from));
      const st = Math.min(piece.trimStart, total / 2);
      const et = Math.min(piece.trimEnd, total / 2);
      if (st > 0) push(add(piece.from, scaleV(dir, st)));
      if (et > 0) push(sub(piece.to, scaleV(dir, et)));
    }
    return out;
  }, [cutList, toScreen, showWelds]);

  /** North arrow direction, drawn along the +y isometric axis. */
  const northDir = useMemo(() => {
    const a = project({ x: 0, y: 0, z: 0 }, 1);
    const b = project({ x: 0, y: -1, z: 0 }, 1);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const l = Math.hypot(dx, dy) || 1;
    return { x: dx / l, y: dy / l };
  }, []);

  const previewSpec = getSizeSpec(material, size);



  return (
    <div className="relative h-full w-full select-none">
      <svg
        ref={svgRef}
        width={box.w}
        height={box.h}
        className="h-full w-full touch-none"
        style={{
          cursor:
            mode === "pan"
              ? "grab"
              : activeRunId
                ? "crosshair"
                : mode === "branch"
                  ? "copy"
                  : "cell",
        }}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onPointerLeave={(e) => {
          if (e.pointerType === "mouse") setCursor(null);
        }}

      >
        {/* ground grid */}
        <g stroke="hsl(var(--border))" strokeWidth={0.5} opacity={0.5}>
          {gridLines.map((l, i) => (
            <line key={i} x1={l.a.x} y1={l.a.y} x2={l.b.x} y2={l.b.y} />
          ))}
        </g>

        {/* pipe runs */}
        {runs.map((run) => (
          <g key={run.id}>
            <path
              d={runPath(run)}
              fill="none"
              stroke="hsl(var(--foreground))"
              strokeWidth={2}
              strokeLinecap="round"
            />
            {/* dimension labels */}
            {run.pts.slice(0, -1).map((a, i) => {
              const b = run.pts[i + 1];
              const m = toScreen({
                x: (a.x + b.x) / 2,
                y: (a.y + b.y) / 2,
                z: (a.z + b.z) / 2,
              });
              return (
                <text
                  key={i}
                  x={m.x}
                  y={m.y - 6}
                  textAnchor="middle"
                  className="fill-muted-foreground"
                  style={{ fontSize: 10 * (coarse ? 1.3 : 1), fontFamily: "ui-monospace, monospace" }}
                >
                  {formatFtIn(lenV(sub(b, a)))}
                </text>
              );
            })}
            {/* run size tag at origin */}
            {run.pts[0] && (
              <text
                x={toScreen(run.pts[0]).x + 8}
                y={toScreen(run.pts[0]).y + 14}
                className="fill-primary"
                style={{ fontSize: 10 * (coarse ? 1.3 : 1), fontFamily: "ui-monospace, monospace" }}
              >
                {run.segSizes[0] ?? run.size} CS Sch 40
              </text>
            )}
          </g>
        ))}

        {/* fitting symbols */}
        {fittings.map((f, i) => {
          const s = toScreen(f.at);
          if (f.kind === "tee") {
            const spec = getSizeSpec(material, f.size);
            const tip = f.branchDir
              ? toScreen(add(f.at, scaleV(f.branchDir, spec.teeBranch)))
              : s;
            return (
              <g key={i}>
                <line
                  x1={s.x}
                  y1={s.y}
                  x2={tip.x}
                  y2={tip.y}
                  stroke="hsl(var(--foreground))"
                  strokeWidth={2}
                />
                <text
                  x={s.x + 7}
                  y={s.y - 7}
                  className="fill-primary"
                  style={{ fontSize: 9 * (coarse ? 1.3 : 1), fontFamily: "ui-monospace, monospace" }}
                >
                  {f.size} TEE
                </text>
              </g>
            );
          }
          if (f.kind === "reducer") {
            return (
              <g key={i}>
                <path
                  d={`M ${s.x - 5} ${s.y - 5} L ${s.x + 5} ${s.y - 2} L ${s.x + 5} ${
                    s.y + 2
                  } L ${s.x - 5} ${s.y + 5} Z`}
                  fill="hsl(var(--background))"
                  stroke="hsl(var(--primary))"
                  strokeWidth={1.4}
                />
                <text
                  x={s.x + 8}
                  y={s.y - 6}
                  className="fill-primary"
                  style={{ fontSize: 9 * (coarse ? 1.3 : 1), fontFamily: "ui-monospace, monospace" }}
                >
                  {f.size}×{f.sizeOut} RED
                </text>
              </g>
            );
          }
          return (
            <g key={i}>
              <text
                x={s.x + 7}
                y={s.y - 7}
                className="fill-primary"
                style={{ fontSize: 9 * (coarse ? 1.3 : 1), fontFamily: "ui-monospace, monospace" }}
              >
                {f.angle}° ELL
              </text>
            </g>
          );
        })}

        {/* butt-weld symbols (solid dots, ISO single-line convention) */}
        {weldPoints.map((w, i) => (
          <circle
            key={`w${i}`}
            cx={w.x}
            cy={w.y}
            r={2.6 * mk}
            fill="hsl(var(--foreground))"
            stroke="hsl(var(--background))"
            strokeWidth={0.8}
          />
        ))}


        {/* endpoints */}
        {runs.map((run) => {
          const ends = run.branch
            ? [run.pts[run.pts.length - 1]]
            : [run.pts[0], run.pts[run.pts.length - 1]];
          return ends.map((p, i) => {
            const s = toScreen(p);
            return (
              <rect
                key={`${run.id}-${i}`}
                x={s.x - 3 * mk}
                y={s.y - 3 * mk}
                width={6 * mk}
                height={6 * mk}
                fill="hsl(var(--background))"
                stroke="hsl(var(--foreground))"
                strokeWidth={1.2 * mk}
              />
            );
          });
        })}

        {/* branch target indicator */}
        {branchTarget && (
          <g>
            <circle
              cx={toScreen(branchTarget.at).x}
              cy={toScreen(branchTarget.at).y}
              r={6 * mk}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth={2 * mk}
            />
            {branchTarget.snapped === "vertex" && (
              <circle
                cx={toScreen(branchTarget.at).x}
                cy={toScreen(branchTarget.at).y}
                r={10 * mk}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth={1 * mk}
                strokeDasharray="3 3"
              />
            )}
            <text
              x={toScreen(branchTarget.at).x + 12}
              y={toScreen(branchTarget.at).y - 9}
              className="fill-primary"
              style={{ fontSize: 10 * (coarse ? 1.3 : 1), fontFamily: "ui-monospace, monospace" }}
            >
              {branchTarget.snapped === "vertex" ? "TEE @ VERTEX" : "TEE HERE"}
            </text>

          </g>
        )}

        {/* snap guides along every lockable axis */}
        {axisGuides.map((g) => (
          <g key={g.axis} opacity={g.active ? 0.9 : 0.32}>
            <line
              x1={g.from.x}
              y1={g.from.y}
              x2={g.to.x}
              y2={g.to.y}
              stroke="hsl(var(--primary))"
              strokeWidth={(g.active ? 1.2 : 0.8) * mk}
              strokeDasharray={g.active ? "2 6" : "1 7"}
            />
            {g.ticks.map((t, i) => (
              <circle key={i} cx={t.x} cy={t.y} r={(g.active ? 1.6 : 1) * mk} fill="hsl(var(--primary))" />
            ))}
          </g>
        ))}

        {/* vertices a branch can lock onto */}
        {branchVertices.map((v, i) => (
          <path
            key={i}
            d={`M ${v.x} ${v.y - 5 * mk} L ${v.x + 5 * mk} ${v.y} L ${v.x} ${v.y + 5 * mk} L ${v.x - 5 * mk} ${v.y} Z`}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth={1 * mk}
            opacity={0.6}
          />
        ))}

        {/* live preview */}
        {lastPt && preview && (
          <g>
            {turnMarker && turnMarker.d && (
              <>
                <path
                  d={turnMarker.d}
                  fill="none"
                  stroke="hsl(var(--primary))"
                  strokeWidth={1.2}
                />
                <text
                  x={turnMarker.at.x + 10}
                  y={turnMarker.at.y + 18}
                  className="fill-primary"
                  style={{ fontSize: 9 * (coarse ? 1.3 : 1), fontFamily: "ui-monospace, monospace" }}
                >
                  {turnMarker.label}
                </text>
              </>
            )}
            <circle
              cx={toScreen(preview.to).x}
              cy={toScreen(preview.to).y}
              r={5 * mk}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth={1.5 * mk}
            />

            <line
              x1={toScreen(lastPt).x}
              y1={toScreen(lastPt).y}
              x2={toScreen(preview.to).x}
              y2={toScreen(preview.to).y}
              stroke="hsl(var(--primary))"
              strokeWidth={2 * mk}
              strokeDasharray="5 4"
            />
            <text
              x={(toScreen(lastPt).x + toScreen(preview.to).x) / 2}
              y={(toScreen(lastPt).y + toScreen(preview.to).y) / 2 - 8}
              textAnchor="middle"
              className="fill-primary"
              style={{ fontSize: 11 * (coarse ? 1.3 : 1), fontFamily: "ui-monospace, monospace" }}
            >
              {formatFtIn(preview.inches)} · {axisLabel[preview.axis]} · {size}
              {allowedAxes.length < 6 ? " · 90° LOCK" : ""}
            </text>
          </g>
        )}

        {/* start cursor when nothing is being drawn */}
        {!lastPt && cursor && mode === "draw" && (
          <circle
            cx={toScreen(groundPoint(cursor.x, cursor.y)).x}
            cy={toScreen(groundPoint(cursor.x, cursor.y)).y}
            r={4 * mk}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth={1.5 * mk}
          />
        )}

        {/* north arrow — standard ISO orientation marker */}
        <g
          transform={`translate(${box.w - 46} 34)`}
          stroke="hsl(var(--muted-foreground))"
          fill="none"
          strokeWidth={1.2}
        >
          <line x1={0} y1={0} x2={northDir.x * 24} y2={northDir.y * 24} />
          <path
            d={`M ${northDir.x * 24} ${northDir.y * 24} l ${-northDir.x * 8 + northDir.y * 4} ${
              -northDir.y * 8 - northDir.x * 4
            } l ${northDir.y * -8} ${northDir.x * 8} Z`}
            fill="hsl(var(--muted-foreground))"
            stroke="none"
          />
          <text
            x={northDir.x * 32}
            y={northDir.y * 32 + 3}
            textAnchor="middle"
            className="fill-muted-foreground"
            stroke="none"
            style={{ fontSize: 10, fontFamily: "ui-monospace, monospace" }}
          >
            N
          </text>
        </g>
      </svg>


      <div className="pointer-events-none absolute left-2 top-2 rounded-md bg-background/85 px-2 py-1 text-[10px] font-mono text-muted-foreground">
        {previewSpec.nps} A106 Sch 40 · OD {previewSpec.od}" · {previewSpec.lbPerFt} lb/ft ·
        {" "}90° {previewSpec.elbow90}" · tee {previewSpec.teeRun}"
      </div>

      {/* symbol legend */}
      <div className="pointer-events-none absolute left-2 top-9 rounded-md bg-background/85 px-2 py-1 font-mono text-[9px] text-muted-foreground">
        <svg width={196} height={42} aria-hidden>
          <g stroke="hsl(var(--foreground))" strokeWidth={1.6} fill="none">
            <line x1={4} y1={11} x2={24} y2={11} />
            <circle cx={14} cy={11} r={2.6} fill="hsl(var(--foreground))" strokeWidth={0} />
            <path d="M 82 17 L 82 11 Q 82 5 88 5" />
            <line x1={134} y1={11} x2={154} y2={11} />
            <line x1={144} y1={11} x2={144} y2={4} />
          </g>
          <path
            d="M 6 37 L 18 34 L 18 30 L 6 27 Z"
            fill="hsl(var(--background))"
            stroke="hsl(var(--primary))"
            strokeWidth={1.2}
          />
          <g className="fill-muted-foreground" style={{ fontSize: 8 }}>
            <text x={30} y={14}>BW WELD</text>
            <text x={94} y={14}>ELL</text>
            <text x={160} y={14}>TEE</text>
            <text x={24} y={36}>REDUCER</text>
            <text x={78} y={36}>SCH 40 · A106</text>
          </g>
        </svg>

      </div>

      {mode === "branch" && !activeRunId && (
        <div className="pointer-events-none absolute right-2 top-2 rounded-md bg-primary/10 px-2 py-1 text-[10px] font-mono text-primary">
          Branch mode — click on a pipe to tee off
        </div>
      )}
      {mode === "pan" && (
        <div className="pointer-events-none absolute right-2 top-2 rounded-md bg-primary/10 px-2 py-1 text-[10px] font-mono text-primary">
          Pan mode — drag to move the view, nothing is drawn
        </div>
      )}

      {/* gesture help overlay */}
      {showHelp && (
        <div
          onPointerEnter={clearHelpTimer}
          className="absolute bottom-2 left-2 right-14 max-w-sm rounded-md border border-border bg-background/95 p-3 text-[11px] shadow-lg"
        >
          <div className="mb-1 flex items-center justify-between">
            <span className="font-semibold text-foreground">
              {modeHelp.title}
            </span>
            <button
              type="button"
              aria-label="Close gesture help"
              onClick={closeHelp}
              className="rounded px-1 text-muted-foreground hover:text-foreground"
            >
              ✕
            </button>
          </div>
          <ul className="space-y-0.5 text-muted-foreground">
            {modeHelp.lines.map((l) => (
              <li key={l} className="flex gap-1.5">
                <span className="text-primary">·</span>
                <span>{l}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="absolute bottom-2 right-2 flex flex-col gap-1">
        <button
          type="button"
          aria-label="Toggle weld symbols"
          aria-pressed={showWelds}
          onClick={() => setShowWelds((v) => !v)}
          className={`flex h-9 w-9 items-center justify-center rounded-md border border-border text-[10px] font-semibold shadow-sm active:scale-95 ${
            showWelds ? "bg-primary text-primary-foreground" : "bg-background/90 text-foreground"
          }`}
        >
          BW
        </button>
        <button
          type="button"
          aria-label="Zoom in"
          onClick={() => zoomBy(1.25)}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background/90 text-base font-semibold text-foreground shadow-sm active:scale-95"
        >
          +
        </button>
        <button
          type="button"
          aria-label="Zoom out"
          onClick={() => zoomBy(0.8)}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background/90 text-base font-semibold text-foreground shadow-sm active:scale-95"
        >
          −
        </button>
        <button
          type="button"
          aria-label="Show gesture help"
          aria-expanded={showHelp}
          onClick={() => (showHelp ? closeHelp() : openHelp())}
          className={`flex h-9 w-9 items-center justify-center rounded-md border border-border text-base font-semibold shadow-sm active:scale-95 ${
            showHelp ? "bg-primary text-primary-foreground" : "bg-background/90 text-foreground"
          }`}
        >
          ?
        </button>
        <button
          type="button"
          aria-label="Fit drawing to view"
          onClick={fitView}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background/90 text-[10px] font-semibold text-foreground shadow-sm active:scale-95"
        >
          FIT
        </button>
      </div>
    </div>
  );
}
